/**
 * SlipstreamSkillEffect — Specter wraith-path apotheosis: temporal slip.
 *
 * For 6 seconds the player exists slightly out of sync with the room.
 * Movement speed doubled, all dashes free (no charge cost), passing through
 * enemies resets their aggro, and afterimages trail continuously behind the
 * player that enemies investigate.
 *
 * Instant activation. Very long cooldown. Fizzle at low XP. Costs 2 slots.
 *
 * Duration window pattern follows DreadSovereign: activate sets
 * `_durationRemaining`, each update tick decrements, window end reverts all
 * temporary modifiers.
 *
 * Implements SkillEffect directly. Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Slipstream
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SlipstreamDeps {
  readonly targeting: {
    shouldFizzle(): boolean;
  };
  readonly player: {
    /** Set the movement speed multiplier on the player entity. */
    setMovementSpeedMultiplier(value: number): void;
    /** Current player world position. */
    getPosition(): { x: number; y: number };
    /** Whether the player is currently moving (has nonzero velocity). */
    isMoving(): boolean;
  };
  readonly dash: {
    /** Toggle whether dash charge consumption is suppressed. */
    suppressDashChargeCost: boolean;
  };
  readonly detection: {
    /**
     * Return enemy IDs whose bodies overlap the given circle.
     * Used for continuous pass-through detection around the player.
     */
    getEnemiesInRadius(radiusPx: number): string[];
    /** Current player world position. */
    getPlayerPosition(): { x: number; y: number };
  };
  readonly ai: {
    /** Reset an enemy's aggro to zero. */
    resetAggro(enemyId: string): void;
  };
  readonly afterimage?: {
    /** Spawn an investigation decoy at the given world position. */
    spawnDecoy(x: number, y: number, durationMs: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SlipstreamConfig {
  readonly cooldownMs: number;
  readonly durationMs: number;
  /** Speed multiplier applied during the window (2.0 = double speed). */
  readonly speedMultiplier: number;
  /** Radius in pixels for pass-through aggro reset detection. */
  readonly aggroResetRadiusPx: number;
  /** Interval in ms between afterimage spawns during the window. */
  readonly afterimageIntervalMs: number;
  /** Duration in ms for each spawned afterimage decoy. */
  readonly afterimageDurationMs: number;
}

const DEFAULT_CONFIG: SlipstreamConfig = {
  cooldownMs: 5000,               // very long cooldown tier
  durationMs: 6000,               // 6-second window
  speedMultiplier: 2.0,           // double movement speed
  aggroResetRadiusPx: 24,         // ~0.75 tiles — tight pass-through radius
  afterimageIntervalMs: 200,      // trail every 200ms while moving
  afterimageDurationMs: 2000,     // each afterimage lingers 2 seconds
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SlipstreamSkillEffect implements SkillEffect {
  readonly skillId = 'slipstream';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: SlipstreamDeps;
  private readonly config: SlipstreamConfig;

  private _cooldownRemaining = 0;
  private _durationRemaining = 0;
  private _fizzled = false;
  private _activationCount = 0;

  /** Time accumulated since the last afterimage spawn. */
  private _afterimageTimer = 0;

  /**
   * Tracks enemies whose aggro has already been reset during the current
   * activation window. Prevents re-resetting on consecutive frames while
   * the player stands near the same enemy.
   */
  private _resetEnemyIds: Set<string> = new Set();

  constructor(deps: SlipstreamDeps, config: Partial<SlipstreamConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }
  get durationRemaining(): number { return this._durationRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && this._durationRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Begin the Slipstream window — cooldown starts when window expires
    this._durationRemaining = this.config.durationMs;
    this._afterimageTimer = 0;
    this._resetEnemyIds.clear();
    this._activationCount++;

    // Apply immediate modifiers
    this.deps.player.setMovementSpeedMultiplier(this.config.speedMultiplier);
    this.deps.dash.suppressDashChargeCost = true;

    return true;
  }

  deactivate(): void {
    // Instant activation — no manual toggle deactivation
  }

  isActive(): boolean {
    return this._durationRemaining > 0;
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Active window processing
    if (this._durationRemaining > 0) {
      this._durationRemaining = Math.max(0, this._durationRemaining - delta);

      if (this._durationRemaining > 0) {
        // --- Aggro reset on pass-through ---
        this._scanAndResetAggro();

        // --- Continuous afterimage trail ---
        this._tickAfterimages(delta);
      } else {
        // Window expired — revert all modifiers
        this._revertModifiers();
      }
    }
  }

  destroy(): void {
    // Clean revert if destroyed while active
    if (this._durationRemaining > 0) {
      this._revertModifiers();
    }
    this._cooldownRemaining = 0;
    this._durationRemaining = 0;
    this._resetEnemyIds.clear();
  }

  // --- Private ---

  /** Revert all temporary modifiers applied during the Slipstream window. */
  private _revertModifiers(): void {
    this.deps.player.setMovementSpeedMultiplier(1);
    this.deps.dash.suppressDashChargeCost = false;
    this._durationRemaining = 0;
    this._cooldownRemaining = this.config.cooldownMs;
    this._resetEnemyIds.clear();
  }

  /** Detect enemies near the player and reset aggro for new contacts. */
  private _scanAndResetAggro(): void {
    const enemies = this.deps.detection.getEnemiesInRadius(this.config.aggroResetRadiusPx);
    for (const enemyId of enemies) {
      if (!this._resetEnemyIds.has(enemyId)) {
        this.deps.ai.resetAggro(enemyId);
        this._resetEnemyIds.add(enemyId);
      }
    }
  }

  /** Spawn afterimages at intervals while the player is moving. */
  private _tickAfterimages(delta: number): void {
    if (!this.deps.afterimage) return;
    if (!this.deps.player.isMoving()) return;

    this._afterimageTimer += delta;
    if (this._afterimageTimer >= this.config.afterimageIntervalMs) {
      this._afterimageTimer -= this.config.afterimageIntervalMs;
      const pos = this.deps.player.getPosition();
      this.deps.afterimage.spawnDecoy(pos.x, pos.y, this.config.afterimageDurationMs);
    }
  }
}
